import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, TextField, Grid, Card, CardContent, 
  Chip, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, 
  ToggleButton, ToggleButtonGroup, Select, MenuItem, FormControl, InputLabel,
  InputAdornment, Avatar, Divider, Grow, TablePagination
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../../config';
import SearchIcon from '@mui/icons-material/Search';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import FolderIcon from '@mui/icons-material/Folder';
import BusinessIcon from '@mui/icons-material/Business';
import AddIcon from '@mui/icons-material/Add';
import PublicIcon from '@mui/icons-material/Public';
import Loading from '../../components/Loading';
import GetAppIcon from '@mui/icons-material/GetApp';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import DashboardIcon from '@mui/icons-material/Dashboard';
import { LinearProgress } from '@mui/material';

function Quotes() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [filter, setFilter] = useState('');
  const [countryFilters, setCountryFilters] = useState([]);
  const [divisionFilters, setDivisionFilters] = useState([]);
  const [search, setSearch] = useState('');
  const [initialLoad, setInitialLoad] = useState(true);
  const [countries, setCountries] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [sortField, setSortField] = useState('createdon');
  const [sortDirection, setSortDirection] = useState('desc');
  const [winPredictions, setWinPredictions] = useState({});
  const navigate = useNavigate();

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchQuotes = async (isSearch = false) => {
    if (!isSearch) setLoading(true);
    else setLoadingMore(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      countryFilters.forEach(c => params.append('country', c));
      divisionFilters.forEach(d => params.append('division', d));

      const res = await axios.get(`${API_URL}/quotes?${params.toString()}`, { headers });
      setQuotes(res.data);

      // Fetch win predictions for non-closed quotes
      const nonClosedIds = res.data
        .filter(q => q.statecode !== 2 && q.statecode !== 3)
        .map(q => q.quoteid);
      if (nonClosedIds.length > 0) {
        try {
          const predRes = await axios.post(`${API_URL}/quotes/batch-predict`, { quote_ids: nonClosedIds }, { headers });
          setWinPredictions(predRes.data.predictions || {});
        } catch (predErr) {
          console.error('Win prediction fetch error:', predErr);
          setWinPredictions({});
        }
      } else {
        setWinPredictions({});
      }
    } catch (err) {
      console.error(err);
    } finally {
      if (!isSearch) setLoading(false);
      else setLoadingMore(false);
    }
  };

  // Fetch quote list immediately — no longer blocked by filter options
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchQuotes(!initialLoad);
      setPage(0);
      if (initialLoad) setInitialLoad(false);
    }, 100);

    return () => clearTimeout(delayDebounceFn);
  }, [search, countryFilters, divisionFilters, dateFrom, dateTo]);

  // Fetch filter dropdowns in the background — does NOT block quote rendering
  useEffect(() => {
    const loadFilters = async () => {
      try {
        const [cRes, dRes] = await Promise.all([
          axios.get(`${API_URL}/quotes/countries`, { headers }),
          axios.get(`${API_URL}/quotes/divisions`, { headers })
        ]);
        const allCountries = [...new Set((cRes.data || []).map(c => c.trim()).filter(Boolean))].sort();
        const allDivisions = [...new Set((dRes.data || []).map(d => d.trim()).filter(Boolean))].sort();
        setCountries(allCountries);
        setDivisions(allDivisions);
        // Backend now handles role-based country/division filtering — no client-side pre-selection needed
      } catch (e) {
        console.error('Failed to load filter options:', e);
      }
    };

    loadFilters();
  }, []);

  if (loading) return <Loading message="Loading Quotes..." />;

  const getStatusColor = (status) => {
    const s = (status || '').toLowerCase();
    if (s.includes('won')) return 'success';
    if (s.includes('lost')) return 'error';
    if (s.includes('active')) return 'primary';
    if (s.includes('draft')) return 'default';
    return 'info';
  };

  const getRefColor = (ref) => {
    const palette = [
      { bg: 'linear-gradient(135deg, #6366f1 0%, #818cf8 100%)', shadow: 'rgba(99,102,241,0.35)' },
      { bg: 'linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%)', shadow: 'rgba(14,165,233,0.35)' },
      { bg: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)', shadow: 'rgba(16,185,129,0.35)' },
      { bg: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)', shadow: 'rgba(245,158,11,0.35)' },
      { bg: 'linear-gradient(135deg, #ef4444 0%, #f87171 100%)', shadow: 'rgba(239,68,68,0.35)' },
      { bg: 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)', shadow: 'rgba(139,92,246,0.35)' },
      { bg: 'linear-gradient(135deg, #ec4899 0%, #f472b6 100%)', shadow: 'rgba(236,72,153,0.35)' },
      { bg: 'linear-gradient(135deg, #14b8a6 0%, #2dd4bf 100%)', shadow: 'rgba(20,184,166,0.35)' },
    ];
    if (!ref) return palette[0];
    const code = ref.charCodeAt(0) || 0;
    return palette[code % palette.length];
  };

  // Extract unique statuses from actual quotes for dynamic filter dropdown
  const uniqueStatuses = [...new Set(quotes.map(q => q.status || 'Draft').filter(Boolean))].sort();

  const filteredQuotes = quotes.filter(q => {
    if (!filter) return true;
    return (q.status || 'Draft') === filter;
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setPage(0);
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <UnfoldMoreIcon sx={{ fontSize: 16, color: '#cbd5e1', ml: 0.5 }} />;
    return sortDirection === 'asc' 
      ? <ArrowUpwardIcon sx={{ fontSize: 16, color: '#3b82f6', ml: 0.5 }} />
      : <ArrowDownwardIcon sx={{ fontSize: 16, color: '#3b82f6', ml: 0.5 }} />;
  };

  // Sorted newest first by default, or by selected field
  const sortedQuotes = [...filteredQuotes].sort((a, b) => {
    let valA, valB;
    switch (sortField) {
      case 'name':
        valA = (a.name || '').toLowerCase();
        valB = (b.name || '').toLowerCase();
        break;
      case 'customer_name':
        valA = (a.customer_name || '').toLowerCase();
        valB = (b.customer_name || '').toLowerCase();
        break;
      case 'country':
        valA = (a.country || '').toLowerCase();
        valB = (b.country || '').toLowerCase();
        break;
      case 'division':
        valA = (a.division || '').toLowerCase();
        valB = (b.division || '').toLowerCase();
        break;
      case 'total_amount':
        valA = a.total_amount || 0;
        valB = b.total_amount || 0;
        break;
      case 'status':
        valA = (a.status || '').toLowerCase();
        valB = (b.status || '').toLowerCase();
        break;
      case 'closed_status':
        valA = (a.closed_status || '').toLowerCase();
        valB = (b.closed_status || '').toLowerCase();
        break;
      case 'win_probability':
        var predA = winPredictions[a.quoteid] || winPredictions[(a.quoteid || '').toUpperCase()] || null;
        var predB = winPredictions[b.quoteid] || winPredictions[(b.quoteid || '').toUpperCase()] || null;
        valA = (predA && predA.win_probability != null) ? predA.win_probability : -1;
        valB = (predB && predB.win_probability != null) ? predB.win_probability : -1;
        break;
      case 'createdon':
      default:
        valA = a.createdon || '';
        valB = b.createdon || '';
        break;
    }
    if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
    if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const formatCurrency = (amount) => {
    if (!amount) return '$0';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const handleExport = () => {
    if (sortedQuotes.length === 0) return;
    const csvHeaders = ['Quote Name', 'Quote Number', 'Quote Ref', 'Customer', 'Status', 'Total Amount', 'Country', 'Division', 'Created On'];
    const csvRows = [csvHeaders.join(',')];
    sortedQuotes.forEach(q => {
      const row = [
        `"${(q.name || '').replace(/"/g, '""')}"`,
        `"${(q.quote_number || '').replace(/"/g, '""')}"`,
        `"${(q.quote_ref || '').replace(/"/g, '""')}"`,
        `"${(q.customer_name || '').replace(/"/g, '""')}"`,
        `"${(q.status || '').replace(/"/g, '""')}"`,
        `${q.total_amount || 0}`,
        `"${(q.country || '').replace(/"/g, '""')}"`,
        `"${(q.division || '').replace(/"/g, '""')}"`,
        `"${formatDate(q.createdon)}"`
      ];
      csvRows.push(row.join(','));
    });
    const csvContent = "data:text/csv;charset=utf-8," + csvRows.join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "quotes_export.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // For list view pagination
  const displayedQuotes = sortedQuotes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden', boxSizing: 'border-box', pb: 6, px: { xs: 1, md: 3 }, bgcolor: '#f4f6f8', minHeight: '100vh' }}>
      
      {/* Modern Gradient Header */}
      <Grow in={true} timeout={500}>
        <Box sx={{ 
            mb: 4, 
            mt: 2, 
            p: 4, 
            borderRadius: 4, 
            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', 
            color: 'white',
            boxShadow: '0 10px 30px rgba(30, 60, 114, 0.2)',
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: 3
        }}>
            <Box sx={{ position: 'relative', zIndex: 2 }}>
                <Typography variant="h4" fontWeight="800" sx={{ mb: 1, letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 2, color: 'white' }}>
                    <RequestQuoteIcon sx={{ fontSize: 36, color: '#90caf9' }} />
                    Quotes
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    View and manage all quotes from Dynamics 365 CRM
                </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2, position: 'relative', zIndex: 2 }}>
                <Button 
                    variant="contained" 
                    startIcon={<AddIcon />} 
                    onClick={() => navigate('/quotes/new')}
                    sx={{ 
                        borderRadius: 2, 
                        fontWeight: 'bold', 
                        px: 3, 
                        py: 1,
                        bgcolor: 'white',
                        color: '#1e3c72',
                        boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
                        '&:hover': { bgcolor: '#f0f0f0', transform: 'translateY(-2px)' },
                        transition: 'all 0.2s'
                    }}
                >
                    New
                </Button>
                <Button 
                    variant="contained" 
                    startIcon={<AutoGraphIcon />} 
                    onClick={() => {
                        const ids = quotes
                            .filter(q => q.closed_status && (q.closed_status.toLowerCase().includes('won') || q.closed_status.toLowerCase().includes('lost')))
                            .map(q => q.quoteid);
                        const wonCount = quotes.filter(q => q.closed_status && q.closed_status.toLowerCase().includes('won')).length;
                        const lostCount = quotes.filter(q => q.closed_status && q.closed_status.toLowerCase().includes('lost')).length;
                        console.log(`Strategic Plan: sending ${ids.length} quote IDs (${wonCount} won + ${lostCount} lost from ${quotes.length} total)`);
                        navigate('/quotes/strategic-plan', { state: { quoteIds: ids } });
                    }}
                    sx={{
                        borderRadius: 2, 
                        fontWeight: 'bold', 
                        px: 3, 
                        py: 1,
                        bgcolor: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        boxShadow: 'none',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                    }}
                >
                    AI Plan
                </Button>
                <Button 
                    variant="contained" 
                    startIcon={<DashboardIcon />} 
                    onClick={() => {
                        const allQuotes = filteredQuotes;
                        const openQuotes = allQuotes.filter(q => q.statecode !== 2 && q.statecode !== 3);
                        console.log(`Quote Dashboard: sending ${openQuotes.length} open quotes (from ${allQuotes.length} total)`);
                        navigate('/quotes/dashboard', {
                            state: { 
                                quotes: allQuotes,
                                openQuoteIds: openQuotes.map(q => q.quoteid)
                            } 
                        });
                    }}
                    disabled={filteredQuotes.length === 0}
                    sx={{ 
                        borderRadius: 2, 
                        fontWeight: 'bold', 
                        px: 3, 
                        py: 1,
                        bgcolor: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        boxShadow: 'none',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                    }}
                >
                    Pipeline
                </Button>
                <Button 
                    variant="contained" 
                    startIcon={<GetAppIcon />} 
                    onClick={handleExport}
                    disabled={sortedQuotes.length === 0}
                    sx={{ 
                        borderRadius: 2, 
                        fontWeight: 'bold', 
                        px: 3, 
                        py: 1,
                        bgcolor: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        boxShadow: 'none',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                    }}
                >
                    Export
                </Button>
            </Box>
            
            {/* Decorative background circles */}
            <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
            <Box sx={{ position: 'absolute', bottom: -100, right: 100, width: 250, height: 250, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
        </Box>
      </Grow>

      {loadingMore && <LinearProgress sx={{ mb: 2, borderRadius: 2 }} />}

      {/* Filters and Controls */}
      <Card elevation={0} sx={{ mb: 4, p: 2, borderRadius: 4, border: '1px solid #f1f5f9', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'center' }, gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Status</InputLabel>
                    <Select 
                        value={filter} 
                        label="Status" 
                        onChange={e => { setFilter(e.target.value); setPage(0); }}
                        sx={{ borderRadius: 2, bgcolor: '#f8fafc', '& fieldset': { borderColor: '#e2e8f0' } }}>
                        <MenuItem value="">All</MenuItem>
                        {uniqueStatuses.map(st => <MenuItem key={st} value={st}>{st}</MenuItem>)}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Country</InputLabel>
                    <Select 
                        multiple 
                        value={countryFilters} 
                        label="Country" 
                        onChange={e => setCountryFilters(e.target.value)}
                        renderValue={(selected) => selected.length === 0 ? 'All' : selected.join(', ')}
                        sx={{ borderRadius: 2, bgcolor: '#f8fafc', '& fieldset': { borderColor: '#e2e8f0' } }}>
                        {countries.map((c, i) => <MenuItem key={i} value={c}>{c}</MenuItem>)}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Division</InputLabel>
                    <Select 
                        multiple 
                        value={divisionFilters} 
                        label="Division" 
                        onChange={e => setDivisionFilters(e.target.value)}
                        renderValue={(selected) => selected.length === 0 ? 'All' : selected.join(', ')}
                        sx={{ borderRadius: 2, bgcolor: '#f8fafc', '& fieldset': { borderColor: '#e2e8f0' } }}>
                        {divisions.map((d, i) => <MenuItem key={i} value={d}>{d}</MenuItem>)}
                    </Select>
                </FormControl>
                <TextField 
                    placeholder="Search quotes..." 
                    size="small" 
                    value={search} 
                    onChange={e => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8' }} /></InputAdornment>
                    }}
                    sx={{ 
                        flexGrow: 1, 
                        minWidth: { sm: 250 },
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: '#f8fafc',
                            borderRadius: 2,
                            '& fieldset': { borderColor: '#e2e8f0' },
                            '&:hover fieldset': { borderColor: '#cbd5e1' },
                            '&.Mui-focused fieldset': { borderColor: '#3b82f6', borderWidth: '1px' },
                        }
                    }}
                />
                <TextField 
                    type="date"
                    label="From"
                    size="small"
                    value={dateFrom}
                    onChange={e => setDateFrom(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ 
                        minWidth: 150,
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: '#f8fafc',
                            borderRadius: 2,
                            '& fieldset': { borderColor: '#e2e8f0' },
                        }
                    }}
                />
                <TextField 
                    type="date"
                    label="To"
                    size="small"
                    value={dateTo}
                    onChange={e => setDateTo(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ 
                        minWidth: 150,
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: '#f8fafc',
                            borderRadius: 2,
                            '& fieldset': { borderColor: '#e2e8f0' },
                        }
                    }}
                />
                <Box sx={{ display: 'flex', gap: 2, height: '40px' }}>
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={(e, newMode) => { if (newMode) setViewMode(newMode); }}
                  size="small"
                  sx={{ 
                      '& .MuiToggleButton-root': { 
                          color: '#94a3b8',
                          borderColor: '#e2e8f0',
                          '&.Mui-selected': { 
                              bgcolor: '#eff6ff', 
                              color: '#3b82f6',
                              borderColor: '#bfdbfe'
                          },
                          '&:hover': { bgcolor: '#f8fafc' }
                      }
                  }}
                >
                  <ToggleButton value="list"><ViewListIcon fontSize="small" /></ToggleButton>
                  <ToggleButton value="grid"><ViewModuleIcon fontSize="small" /></ToggleButton>
                </ToggleButtonGroup>
                </Box>
            </Box>
        </Box>
      </Card>

      {/* Quotes content */}
      {sortedQuotes.length === 0 ? (
          <Paper elevation={0} sx={{ p: 6, textAlign: 'center', border: '2px dashed #e2e8f0', borderRadius: 4, bgcolor: '#f8fafc' }}>
              <FolderIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
              <Typography variant="h6" color="#64748b" fontWeight="700" mb={1}>No quotes found</Typography>
              <Typography variant="body2" color="#94a3b8">Try adjusting your filters or search criteria.</Typography>
          </Paper>
      ) : viewMode === 'grid' ? (
        <>
        <Grid container spacing={3}>
            {sortedQuotes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(q => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={q.quoteid}>
                    <Card elevation={0} sx={{ 
                        height: '100%', 
                        borderRadius: 4, 
                        border: '1px solid #f1f5f9',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.03)', 
                        cursor: 'pointer', 
                        transition: 'all 0.3s ease', 
                        display: 'flex',
                        flexDirection: 'column',
                        '&:hover': { transform: 'translateY(-6px)', boxShadow: '0 12px 28px rgba(0,0,0,0.08)', borderColor: '#cbd5e1' } 
                    }} onClick={() => navigate(`/quotes/${q.quoteid}`)}>
                        <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, overflow: 'hidden' }}>
                                    <Avatar sx={{ 
                                        background: getRefColor(q.quote_ref || q.quote_number).bg,
                                        color: 'white', 
                                        width: 48, 
                                        height: 48, 
                                        fontWeight: '800',
                                        fontSize: '0.72rem',
                                        borderRadius: 2.5,
                                        boxShadow: `0 6px 16px ${getRefColor(q.quote_ref || q.quote_number).shadow}`,
                                        letterSpacing: '0.03em'
                                    }}>
                                        {(q.quote_ref || q.quote_number || 'Q').substring(0, 4)}
                                    </Avatar>
                                    <Box sx={{ overflow: 'hidden' }}>
                                        <Typography variant="caption" sx={{ fontWeight: '800', display: 'block', color: '#3b82f6', letterSpacing: '0.05em' }}>{q.quote_ref || q.quote_number}</Typography>
                                        <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: '800', color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{q.name}</Typography>
                                    </Box>
                                </Box>
                                <Chip 
                                    label={q.status || 'Draft'} 
                                    size="small" 
                                    color={getStatusColor(q.status)} 
                                    sx={{ height: 24, fontSize: '0.75rem', fontWeight: 'bold', borderRadius: 1.5 }} 
                                />
                            </Box>
                            {q.closed_status && (
                                <Box sx={{ mb: 1.5 }}>
                                    <Chip 
                                        label={q.closed_status} 
                                        size="small" 
                                        sx={{ 
                                            height: 22, fontSize: '0.7rem', fontWeight: 'bold', borderRadius: 1.5,
                                            bgcolor: (q.closed_status || '').toLowerCase().includes('won') ? '#dcfce7' : '#fef2f2',
                                            color: (q.closed_status || '').toLowerCase().includes('won') ? '#166534' : '#b91c1c',
                                        }} 
                                    />
                                </Box>
                            )}
                            
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155', display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <BusinessIcon sx={{ fontSize: 16, color: '#94a3b8' }} /> {q.customer_name || 'N/A'}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#64748b', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <PublicIcon sx={{ fontSize: 16, color: '#94a3b8' }} /> {q.country || 'N/A'}
                                </Typography>
                            </Box>

                            <Box sx={{ mt: 'auto' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <AttachMoneyIcon sx={{ fontSize: 18, color: '#10b981' }} />
                                        <Typography variant="h6" sx={{ fontWeight: '800', color: '#10b981', fontSize: '1.05rem' }}>
                                            {formatCurrency(q.total_amount)}
                                        </Typography>
                                    </Box>
                                    <Typography variant="caption" fontWeight="600" color="#64748b">
                                        {formatDate(q.createdon)}
                                    </Typography>
                                </Box>

                                <Box sx={{ pt: 2, borderTop: '1px dashed #e2e8f0' }}>
                                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#94a3b8' }}>
                                        {q.division ? `Division: ${q.division}` : ''}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            ))}
        </Grid>
        {/* Grid Pagination */}
        <TablePagination
            rowsPerPageOptions={[8, 16, 32]}
            component="div"
            count={sortedQuotes.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ mt: 2, '& .MuiTablePagination-toolbar': { borderRadius: 3 } }}
        />
        </>
      ) : (
        <Grow in={true} timeout={1200}>
        <Paper elevation={0} sx={{ borderRadius: 4, overflow: 'hidden', border: 'none', boxShadow: '0 10px 40px rgba(0,0,0,0.06)', width: '100%', bgcolor: 'white' }}>
            <Box sx={{ overflowX: 'auto', width: '100%', display: 'block' }}>
            <Table sx={{ width: '100%', tableLayout: 'auto', '& .MuiTableCell-root': { borderColor: '#f0f0f0', py: 2.5 } }} aria-label="quotes table">
                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                    <TableRow>
                        <TableCell onClick={() => handleSort('name')} sx={{ fontWeight: '800', color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px', cursor: 'pointer', userSelect: 'none', '&:hover': { color: '#3b82f6' }, whiteSpace: 'nowrap' }}>
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>Quote / Reference {getSortIcon('name')}</Box>
                        </TableCell>
                        <TableCell onClick={() => handleSort('customer_name')} sx={{ fontWeight: '800', color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px', cursor: 'pointer', userSelect: 'none', '&:hover': { color: '#3b82f6' }, whiteSpace: 'nowrap' }}>
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>Customer {getSortIcon('customer_name')}</Box>
                        </TableCell>
                        <TableCell onClick={() => handleSort('country')} sx={{ fontWeight: '800', color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px', cursor: 'pointer', userSelect: 'none', '&:hover': { color: '#3b82f6' }, whiteSpace: 'nowrap' }}>
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>Country {getSortIcon('country')}</Box>
                        </TableCell>
                        <TableCell onClick={() => handleSort('division')} sx={{ fontWeight: '800', color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px', cursor: 'pointer', userSelect: 'none', '&:hover': { color: '#3b82f6' }, whiteSpace: 'nowrap' }}>
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>Division {getSortIcon('division')}</Box>
                        </TableCell>
                        <TableCell onClick={() => handleSort('total_amount')} sx={{ fontWeight: '800', color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px', cursor: 'pointer', userSelect: 'none', '&:hover': { color: '#3b82f6' }, whiteSpace: 'nowrap' }}>
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>Amount {getSortIcon('total_amount')}</Box>
                        </TableCell>
                        <TableCell onClick={() => handleSort('status')} sx={{ fontWeight: '800', color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px', cursor: 'pointer', userSelect: 'none', '&:hover': { color: '#3b82f6' }, whiteSpace: 'nowrap' }}>
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>Status {getSortIcon('status')}</Box>
                        </TableCell>
                        <TableCell onClick={() => handleSort('closed_status')} sx={{ fontWeight: '800', color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px', cursor: 'pointer', userSelect: 'none', '&:hover': { color: '#3b82f6' }, whiteSpace: 'nowrap' }}>
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>Result {getSortIcon('closed_status')}</Box>
                        </TableCell>
                        <TableCell onClick={() => handleSort('win_probability')} sx={{ fontWeight: '800', color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px', cursor: 'pointer', userSelect: 'none', '&:hover': { color: '#3b82f6' }, whiteSpace: 'nowrap' }}>
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>AI % {getSortIcon('win_probability')}</Box>
                        </TableCell>
                        <TableCell onClick={() => handleSort('createdon')} sx={{ fontWeight: '800', color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px', cursor: 'pointer', userSelect: 'none', '&:hover': { color: '#3b82f6' }, whiteSpace: 'nowrap' }}>
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>Created {getSortIcon('createdon')}</Box>
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {displayedQuotes.length > 0 ? (
                        displayedQuotes.map((q) => (
                            <TableRow
                                key={q.quoteid}
                                hover
                                sx={{ '&:last-child td, &:last-child th': { border: 0 }, cursor: 'pointer', transition: 'background-color 0.2s, transform 0.1s', '&:hover': { bgcolor: '#f8fbff !important' } }}
                                onClick={() => navigate(`/quotes/${q.quoteid}`)}
                            >
                                <TableCell component="th" scope="row">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                                        <Avatar sx={{ 
                                            background: getRefColor(q.quote_ref || q.quote_number).bg,
                                            color: 'white', 
                                            fontWeight: '900', 
                                            width: 46, height: 46, 
                                            fontSize: '0.68rem',
                                            borderRadius: 2.5,
                                            boxShadow: `0 6px 16px ${getRefColor(q.quote_ref || q.quote_number).shadow}`,
                                            letterSpacing: '0.03em'
                                        }}>
                                            {(q.quote_ref || q.quote_number || 'Q').substring(0, 4)}
                                        </Avatar>
                                        <Box>
                                            <Typography fontWeight="700" sx={{ color: '#1e293b', fontSize: '0.95rem' }}>{q.name}</Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                                <Typography variant="caption" sx={{ fontWeight: '800', color: '#3b82f6', bgcolor: '#eff6ff', px: 1, py: 0.25, borderRadius: 1.5 }}>{q.quote_ref || q.quote_number}</Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ color: '#334155', fontWeight: 600, fontSize: '0.85rem' }}>{q.customer_name}</TableCell>
                                <TableCell>
                                    <Typography variant="body2" fontWeight="600" color="#334155">{q.country || '—'}</Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" fontWeight="600" color="#334155">{q.division || '—'}</Typography>
                                </TableCell>
                                <TableCell sx={{ color: '#10b981', fontWeight: 700, fontSize: '0.85rem' }}>{formatCurrency(q.total_amount)}</TableCell>
                        <TableCell>
                                    <Chip label={q.status || 'Draft'} size="small" color={getStatusColor(q.status)} sx={{ height: 26, fontSize: '0.75rem', fontWeight: 'bold', borderRadius: '6px', px: 1 }} />
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                                    {q.closed_status ? (
                                        <Chip label={q.closed_status} size="small" sx={{ height: 24, fontSize: '0.7rem', fontWeight: 'bold', borderRadius: '6px', bgcolor: (q.closed_status || '').toLowerCase().includes('won') ? '#dcfce7' : '#fef2f2', color: (q.closed_status || '').toLowerCase().includes('won') ? '#166534' : '#b91c1c' }} />
                                    ) : (
                                        <Typography variant="body2" fontWeight={600} color="#94a3b8">—</Typography>
                                    )}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                                    {q.statecode === 2 ? (
                                        <Typography variant="body2" fontWeight={700} color="success.main">100%</Typography>
                                    ) : q.statecode === 3 ? (
                                        <Typography variant="body2" fontWeight={700} color="error.main">0%</Typography>
                                    ) : (() => {
                                        var pred = winPredictions[(q.quoteid || '').toUpperCase()] || winPredictions[q.quoteid];
                                        if (pred && pred.win_probability != null && !pred.inactive) {
                                            return <Typography variant="body2" fontWeight={700} color={pred.win_probability >= 50 ? '#10b981' : '#ef4444'}>{pred.win_probability}%</Typography>;
                                        }
                                        return <Typography variant="body2" fontWeight={600} color="#94a3b8">—</Typography>;
                                    })()}
                                </TableCell>
                                <TableCell sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.85rem' }}>{formatDate(q.createdon)}</TableCell>
                            </TableRow>
                        ))
                    ) : (
                        <TableRow>
                            <TableCell colSpan={8} align="center" sx={{ py: 6, color: '#94a3b8', fontWeight: 600 }}>
                                No quotes found matching your criteria.
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
            </Box>
            {/* List Pagination */}
            <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={sortedQuotes.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </Paper>
        </Grow>
      )}

    </Box>
  );
}

export default Quotes;